import React from "react";
import Fuse from "fuse.js";
import CartContext from "./CartContext";

/**
 * ProductPendingPurchaseItems
 *
 * Props:
 *  - storeId (optional) : Guid string
 *  - apiEndpoint (optional) : string (default "ProductPurchasePending")
 *
 * Usage:
 * <ProductPendingPurchaseItems storeId={storeId} />
 * or rely on CartContext: this.context.storeId
 */

export default class ProductPendingPurchaseItems extends React.Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      items: [],
      fuse: null,
      filteredItems: [],
      loading: true,
      error: null,
      search: "",
    };
  }

  componentDidMount() {
    this.loadData();
  }

  apiBase() {
    const base = process.env.REACT_APP_API || "/";
    return base.endsWith("/") ? base : base + "/";
  }

  effectiveStoreId() {
    if (this.props.storeId) return this.props.storeId;
    if (this.context && this.context.storeId) return this.context.storeId;
    return null;
  }

  endpointName() {
    return this.props.apiEndpoint || "ProductPurchasePending";
  }

  loadData() {
    const storeId = this.effectiveStoreId();
    if (!storeId) {
      this.setState({
        loading: false,
        error:
          "storeId not provided. Provide storeId as a prop or via CartContext.",
      });
      return;
    }

    const url = `${this.apiBase()}${this.endpointName()}/pending-items/${encodeURIComponent(
      storeId
    )}`;

    fetch(url, { headers: { "Content-Type": "application/json" } })
      .then((res) => {
        if (!res.ok)
          throw new Error(`Failed to fetch pending items (${res.status})`);
        return res.json();
      })
      .then((data) => {
        const items = Array.isArray(data) ? data : [];

        // Fuse keys: include both PascalCase and camelCase variants to be robust
        const fuse = new Fuse(items, {
          keys: [
            "PurchaseOrderNo",
            "purchaseOrderNo",
            "WarehouseName",
            "warehouseName",
            "SupplierName",
            "supplierName",
            "ItemName",
            "itemName",
            "CreatedBy",
            "createdBy",
          ],
          threshold: 0.35,
          includeScore: false,
        });

        this.setState({
          items,
          fuse,
          filteredItems: items,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        this.setState({
          loading: false,
          error: err.message || "Failed to load",
        });
      });
  }

  formatNumber(val) {
    if (val === null || val === undefined) return "";
    const n = Number(val);
    if (Number.isNaN(n)) return "";
    if (Number.isInteger(n)) return n.toString();
    return n.toFixed(2);
  }

  onSearchChange = (e) => {
    const search = e.target.value;
    this.setState({ search });
    this.applySearch(search);
  };

  applySearch(search) {
    const { fuse, items } = this.state;
    if (!fuse || !search.trim()) {
      this.setState({ filteredItems: items });
      return;
    }
    const results = fuse.search(search.trim());
    this.setState({ filteredItems: results.map((r) => r.item) });
  }

  // CSV helpers
  escapeCsvCell(value) {
    if (value === null || value === undefined) return "";
    if (value instanceof Date) value = value.toISOString();
    const s = String(value);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }

  downloadCsv = () => {
    const { filteredItems } = this.state;
    if (!filteredItems || filteredItems.length === 0) {
      window.alert("No rows to export.");
      return;
    }

    const headers = [
      "PurchaseOrderNo",
      "Created",
      "CreatedBy",
      "WarehouseName",
      "SupplierName",
      "SupplierMobile",
      "ItemName",
      "QtyRequested",
      "QtyReceived",
      "Pending",
    ];

    const rows = filteredItems.map((it) => {
      const createdRaw = it.Created ?? it.created ?? null;
      const createdVal = createdRaw ? new Date(createdRaw).toISOString() : "";
      return [
        it.PurchaseOrderNo ?? it.purchaseOrderNo ?? "",
        createdVal,
        it.CreatedBy ?? it.createdBy ?? "",
        it.WarehouseName ?? it.warehouseName ?? "",
        it.SupplierName ?? it.supplierName ?? "",
        it.SupplierMobile ?? it.supplierMobile ?? "",
        it.ItemName ?? it.itemName ?? "",
        it.QtyRequested ?? it.qtyRequested ?? "",
        it.QtyReceived ?? it.qtyReceived ?? "",
        it.Pending ?? it.pending ?? "",
      ]
        .map((c) => this.escapeCsvCell(c))
        .join(",");
    });

    const bom = "\uFEFF";
    const csv = bom + headers.join(",") + "\n" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const storeId = this.effectiveStoreId() || "store";
    const ts = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .replace("T", "_")
      .split("Z")[0];
    const filename = `product_pending_purchase_${storeId}_${ts}.csv`;

    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  clearSearch = () => {
    this.setState({ search: "" });
    this.applySearch("");
  };

  // Helper to read field robustly (Pascal / camel)
  read = (it, pascal, camel) => it[pascal] ?? it[camel] ?? "";

  render() {
    const { loading, error, filteredItems, search } = this.state;

    return (
      <div className="mx-4 my-3">
        <div className="d-flex align-items-center justify-content-between mb-3 p-2 shadow-sm">
          <h4 className="mb-0">Product Pending Purchase Items</h4>

          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            <input
              type="text"
              className="form-control"
              style={{ minWidth: 300 }}
              placeholder="Search PO / Warehouse / Supplier / Item / CreatedBy"
              value={search}
              onChange={this.onSearchChange}
            />

            <button
              className="btn btn-outline-secondary"
              onClick={this.clearSearch}
              title="Clear search"
            >
              Clear
            </button>

            <button
              className="btn btn-primary"
              onClick={this.downloadCsv}
              title="Download CSV"
            >
              Download CSV
            </button>
          </div>
        </div>

        {loading && <div className="alert alert-info">Loading...</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && (
          <div className="table-responsive">
            <table className="table table-striped table-sm">
              <thead className="table-light">
                <tr>
                  <th>PO No</th>
                  <th>Created</th>
                  <th>CreatedBy</th>
                  <th>Warehouse</th>
                  <th>Supplier</th>
                  <th>Supplier Contact</th>
                  <th>Item</th>
                  <th style={{ textAlign: "right" }}>Qty Requested (A)</th>
                  <th style={{ textAlign: "right" }}>Qty Received (B)</th>
                  <th style={{ textAlign: "right" }}>Pending (A - B)</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center">
                      No pending items found.
                    </td>
                  </tr>
                )}

                {filteredItems.map((it) => {
                  const key = `${
                    this.read(it, "PurchaseRequestId", "purchaseRequestId") ||
                    this.read(it, "purchaseRequestId", "PurchaseRequestId")
                  }-${
                    this.read(it, "ItemId", "itemId") ||
                    this.read(it, "itemId", "ItemId")
                  }`;
                  return (
                    <tr key={key}>
                      <td>
                        {this.read(it, "PurchaseOrderNo", "purchaseOrderNo")}
                      </td>
                      <td>
                        {(() => {
                          const created = it.Created ?? it.created ?? null;
                          return created
                            ? new Date(created).toLocaleString()
                            : "";
                        })()}
                      </td>
                      <td>{this.read(it, "CreatedBy", "createdBy")}</td>
                      <td>{this.read(it, "WarehouseName", "warehouseName")}</td>
                      <td>{this.read(it, "SupplierName", "supplierName")}</td>
                      <td>
                        {this.read(it, "SupplierMobile", "supplierMobile")}
                      </td>
                      <td>{this.read(it, "ItemName", "itemName")}</td>
                      <td style={{ textAlign: "right" }}>
                        {this.formatNumber(it.QtyRequested ?? it.qtyRequested)}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {this.formatNumber(it.QtyReceived ?? it.qtyReceived)}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {this.formatNumber(it.Pending ?? it.pending)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
}
